import type { Context, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useDetection } from '@utility/checkout-utils';

import {
  useAndroidPinchZoom,
  useIOSPinchZoom,
  useTextZoom,
} from './zoom.hooks';
import type {
  MobileZoomContextData,
  MobileZoomContextProviderProps,
} from './zoom.types';

const ZoomContext = createContext<MobileZoomContextData | null>(null);

export const useZoomContext = () => {
  const context = useContext(ZoomContext);

  if (!context) {
    throw new Error(
      'useZoomContext must be used within a <ZoomContextProvider>'
    );
  }

  return context;
};

export const MobileZoomContextProvider = ({
  children,
}: MobileZoomContextProviderProps) => {
  const { isTextZoomed, fontZoom } = useTextZoom();
  const { isPinchZoomed: isAndroidPinchZoomed, scale: androidScale } =
    useAndroidPinchZoom();
  const { isPinchZoomed: isIOSPinchZoomed, scale: iOSScale } =
    useIOSPinchZoom();
  const { isMobile, isIOS: detectIsIOS } = useDetection();

  const contextValue = useMemo(() => {
    if (!isMobile) {
      return {
        isElevatedZoom: false,
        isIntermediateZoom: false,
        isZoomed: false,
      };
    }

    const isOIS = detectIsIOS(navigator.userAgent);
    const isPinchZoomed = isOIS ? isIOSPinchZoomed : isAndroidPinchZoomed;
    const scale = isOIS ? iOSScale : androidScale;

    const ranges = {
      scale: {
        intermediate: scale > 1.5 && scale < 2,
        elevated: scale >= 2,
      },
      fontZoom: {
        intermediate: fontZoom > 1 && fontZoom < 3.2,
        elevated: fontZoom >= 3.2,
      },
    };

    const isElevatedZoom = ranges.scale.elevated || ranges.fontZoom.elevated;

    const isIntermediateZoom =
      isElevatedZoom ||
      ranges.scale.intermediate ||
      ranges.fontZoom.intermediate;

    const isZoomed = isTextZoomed || isPinchZoomed;

    return {
      isZoomed,
      isElevatedZoom,
      isIntermediateZoom,
    };
  }, [
    androidScale,
    detectIsIOS,
    fontZoom,
    iOSScale,
    isAndroidPinchZoomed,
    isIOSPinchZoomed,
    isMobile,
    isTextZoomed,
  ]);

  return (
    <ZoomContext.Provider value={contextValue}>{children}</ZoomContext.Provider>
  );
};

export const withZoomContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => <MobileZoomContextProvider>{children}</MobileZoomContextProvider>;

export const withZoomContextDecorator = (
  data?: Partial<MobileZoomContextData>
) => ({
  context: ZoomContext as Context<MobileZoomContextData>,
  contextValue: ({ args }: { args: Partial<MobileZoomContextData> }) => ({
    isZoomed: false,
    isIntermediateScaleOrFontZoom: false,
    isElevatedScaleOrFontZoom: false,
    ...data,
    ...args,
  }),
});
